import { createContext, useContext } from "react";
import { useLocalObservable } from "mobx-react-lite";

import type { StructuredSearchSchema } from "../../config/structured-search-schema";
import type { Filter } from "../../config/types";

import { StructuredSearchStore, type CompleteDateRange } from "./structured-search-store";

import { requestsSearchSchema } from "@/routes/request-logs/search/requests-search-schema";

const StructuredSearchContext = createContext<StructuredSearchStore | null>(null);

export const StructuredSearchProvider = ({
  children,
  config,
  schema = requestsSearchSchema,
  initialLockedFilters = [],
  initialEditableFilters = [],
  initialQ = "",
  initialDateRange,
  initialUrlHydrationReady = false,
}: {
  children: React.ReactNode;
  /** Domain config; prefer this over `schema` at new call sites. */
  config?: StructuredSearchSchema;
  /** Domain schema; defaults to Request Logs to preserve existing call sites. */
  schema?: StructuredSearchSchema;
  initialLockedFilters?: Filter[];
  /** Non-locked filters applied after locked filters (e.g. copied query into a sheet). */
  initialEditableFilters?: Filter[];
  initialQ?: string;
  initialDateRange?: CompleteDateRange;
  initialUrlHydrationReady?: boolean;
}) => {
  const store = useLocalObservable(() => {
    const resolvedSchema = config ?? schema;
    const instance = new StructuredSearchStore(
      resolvedSchema,
      initialDateRange ? () => initialDateRange : undefined,
      initialLockedFilters,
    );
    if (initialEditableFilters.length > 0) {
      instance.setFiltersFromURL(initialEditableFilters);
    }
    if (initialQ) {
      instance.setSearch(initialQ);
    }
    if (initialUrlHydrationReady) {
      instance.setUrlHydrationReady(true);
    }
    return instance;
  });

  return (
    <StructuredSearchContext.Provider value={store}>{children}</StructuredSearchContext.Provider>
  );
};

export const useStructuredSearchStore = (): StructuredSearchStore => {
  const store = useContext(StructuredSearchContext);
  if (!store) {
    throw new Error("useStructuredSearchStore must be used within StructuredSearchProvider");
  }
  return store;
};
